import { trace } from 'rytm-helpers'
import { gsap, TweenMax, TimelineMax, Power0, Power1, Power2, Power3, Power4, Back, Bounce, Elastic, Expo, Sine, Linear, to } from 'gsap'
import { playSound, stopSound, stopAllSounds } from './soundbank'

const DRIFT_SHOW_HIDE = .5 // perc of viewport width / height (random start pos)
const DEFAULT_TIME_ANIM_SHOW = 2
const DEFAULT_TIME_ANIM_HIDE = 1
const TIME_ANIM_SHOW_DELAY = .5
const FPS = 30

let compositions = []
let compositionsQueue = []
let animations = []
let currentComp, currentCompParams, animTimer
let playing = false
let transforming = true;
let mouseP = {
  x: window.innerWidth / 2,
  y: window.innerHeight / 2
}
// ### Composition Gravity concept:
// attraction param - sets the global (for composition) attraction param
// which is animated in and out...
// for each element there is a gravity factor param for more variations
let compositionGravityInitial = {
  direction: -1,
  attraction: 0
}
let compositionGravity = compositionGravityInitial

// #########
// ### INIT 
export const initPlayground = () => {
  // trace("init playground")
  compositions = [...document.querySelectorAll(".composition-wrapper")]
  compositionsQueue = [...compositions]
  nextComposition()
  document.documentElement.classList.add("composition-transforming")
  addEventListeners()
}
// ### STOP (remove event listeners, etc.)
// playground is still visible. But event listeners should be removed
export const stopPlayground = () => {
  playing = false
  // trace("stop playground")
  removeEventListeners()
  if (currentComp) {
    hideComposition(currentComp)
  }
}
// ### DESTROY
// playground nolonger visible
export const destroyPlayground = () => {
  // trace("destroy playground")
  transforming = false
  compositions = []
  // stop sounds
  stopAllSounds()
  // remove classlists
  // document.body.classList.remove("cursor-active")
  document.documentElement.classList.remove("composition-transforming")
}

// ######################
// ### Playground methods
// add event listeners
const addEventListeners = () => {
  document.addEventListener("click", onDocClick, false)
  document.addEventListener("keydown", onDocKeydown, false)
  document.addEventListener("mousemove", onDocMouseMove, false)
}
// remove event listeners
const removeEventListeners = () => {
  document.removeEventListener("click", onDocClick)
  document.removeEventListener("keydown", onDocKeydown, false)
  document.removeEventListener("mousemove", onDocMouseMove, false)
}
const transformCompositions = () => {
  if (transforming) {
    trace("can't change composition while transforming")
    return false
  }
  if (currentComp) {
    hideComposition(currentComp, nextComposition)
  } else {
    nextComposition()
  }
}
const nextComposition = () => {
  currentComp = getRandomComposition()
  if (currentComp) {
    prepareComposition(currentComp)
  }
}
const getRandomComposition = () => {
  let comp = null

  if (compositionsQueue.length > 1) {
    const rand = Math.floor(Math.random() * compositionsQueue.length)
    comp = compositionsQueue[rand]
    if (currentComp && comp.id == currentComp.id) {
      comp = getRandomComposition()
    }
  } else if (compositionsQueue.length > 0) {
    comp = compositionsQueue[0]
  }
  // subtrackt from comp queue
  compositionsQueue = subtractCompositionFromQueue(comp)
  if (compositionsQueue.length < 1) {
    compositionsQueue = [...compositions]
  }
  trace("random comp:", comp.id)
  return comp
}
const subtractCompositionFromQueue = (comp) => {
  let arr = []
  compositionsQueue.forEach((c, index) => {
    if (comp.id != c.id) {
      arr.push(c)
    }
  })
  return arr
}
// hide composition
const hideComposition = (comp, callback) => {
  // trace("hide comp", comp)
  // stop keyframes animation
  transforming = true
  document.documentElement.classList.add("composition-transforming")
  playing = false
  const elements = getCompositionElements(comp)
  elements.forEach(hideElement)
  // stop composition sound
  stopCompositionSound("bgLoop")
  // composition HIDE sound
  startCompositionSound("hide")
  // timeout for hide
  clearTimeout(animTimer)
  animTimer = setTimeout(() => {
    // stop animations
    stopAnimations()
    comp.classList.remove("active")
    if (typeof callback === 'function') {
      // transforming = false
      callback()
    }
  }, getCurrentAnimTimeHide() * 1000)
  
}
// prepare composition
// scope: prepare all elements and start moving them
const prepareComposition = (comp) => {
  currentCompParams = getCompParams(comp)
  setBgColor(currentCompParams?.bgColor)
  // prepare individual elements
  const elements = getCompositionElements(comp)
  elements.forEach(prepareElement)
  // timeout for show DELAY
  clearTimeout(animTimer)
  animTimer = setTimeout(() => {
    showComposition(comp)
  }, TIME_ANIM_SHOW_DELAY * 1000)
}
// show composition
const showComposition = (comp) => {
  transforming = true
  document.documentElement.classList.add("composition-transforming")
  // trace("show comp", comp)
  const elements = getCompositionElements(comp)
  elements.forEach(showElement)
  comp.classList.add("active")
  // composition SHOW sound
  startCompositionSound("show")
  // start composition bg sound
  startCompositionSound("bgLoop", true)
  // timer for keyframes animation to start
  clearTimeout(animTimer)
  animTimer = setTimeout(() => {
    // start gravity animation
    startCompositionGravity(comp)
    // start keyframe animation
    playing = true
    transforming = false
    document.documentElement.classList.remove("composition-transforming")
    enterAnimationFrame()
  }, getCurrentAnimTimeShow() * 1000)
}
// start composition sound
const startCompositionSound = (key, loop = false) => {
  const fileName = currentCompParams?.sounds[key]
  if (fileName) {
    playSound(fileName, loop)
  }
}
// stop composition sound
const stopCompositionSound = (key) => {
  const fileName = currentCompParams?.sounds[key]
  if (fileName) {
    stopSound(fileName)
  }
}
// start composition gravity...
// stop is handled by stopAnimations
const startCompositionGravity = (comp) => {
  // reset composition gravity
  TweenMax.killTweensOf(compositionGravity)
  TweenMax.set(compositionGravity, compositionGravityInitial)
  const compParams = getCompParams(comp)
  const gravityParams = compParams?.gravity
  if (!gravityParams || !gravityParams.t) {
    trace("composition has no gravity params or unknown")
    return
  }
  const t = new TimelineMax({
    repeat: -1,
    repeatDelay: gravityParams.repeatDelay ? gravityParams.repeatDelay : 0,
    delay: gravityParams.delay ? gravityParams.delay : 0,
    yoyo: gravityParams.yoyo ? gravityParams.yoyo : true
  })
  const tp = {}
  tp.attraction = gravityParams.attraction ? gravityParams.attraction : 0
  tp.onStart = () => {
    // play sound on gravity start cycle
    startCompositionSound("gravityStart")
  }
  tp.onComplete = () => {
    // play sound on gravity complete cycle
    startCompositionSound("gravityComplete")
  }
  tp.ease = getTweenEaseFromString(gravityParams.ease)
  t.to(compositionGravity, gravityParams.t, tp)
  animations.push(t)
}

// prepare composition element (el = <g> with params)
// scope: move out of viewport, start animation
const prepareElement = (el, layer) => { 
  // reset element
  const tp = {
    x: 0, 
    y: 0, 
    transformOrigin:"50% 50%",
    rotation: 0,
    scale: 1
  }
  TweenMax.set(el, tp)
  // set directions data attributes
  el = setElementDirectionsProps(el)
  // get element params
  const params = getElementParams(el)
  // move elements out of viewport
  moveElementOutOfVp(el, params)
  // start levitation
  startElementLevitation(el, params)
}
// move element out of viewport
const moveElementOutOfVp = (el, params) => {
  const p = getElementStartPos(el, params)
  const tp = {
    x: p.x, 
    y: p.y,
    rotation: getElementOutOfVpRotation(el, params),
  }
  TweenMax.set(el, tp)
  // reset subgroup
  const subGroup = el.querySelector("g")
  TweenMax.set(subGroup, {x: 0, y: 0, rotation: 0, scale: 1, transformOrigin:"50% 50%"})
}
const getElementStartPos = (el, params) => {
  const dX = parseInt(el.dataset.directionX)
  const dY = parseInt(el.dataset.directionY)
  let moveX = window.innerWidth * DRIFT_SHOW_HIDE
  let moveY = window.innerHeight * DRIFT_SHOW_HIDE
  let x = 0
  let y = 0
  if (params.masker) {
    // is masker object
    x = dX * (Math.random() * (moveX * .2))
    y = dY * (Math.random() * (moveY * .2))
  } else {
    // not a masker object
    switch (el.dataset.direction) {
      case "top":
        x = dX * (Math.random() * moveX)
        y = - window.innerHeight
        break;
      case "right":
        x = window.innerWidth
        y = dY * (Math.random() * moveY)
        break;
      case "bottom":
        x = dX * (Math.random() * moveX)
        y = window.innerHeight
        break;
      case "left":
        x = - window.innerWidth
        y = dY * (Math.random() * moveY)
        break;
    }
  }
  return {x, y}
}
// set element directions data
// scope: set move in / out directions dataset props
// direction = main direction, directionX/Y = leading horizontal / vertical direct
const setElementDirectionsProps = (el) => {
  if (!el.dataset.direction) {
    const rect = el.getBoundingClientRect()
    const vW = window.innerWidth
    const vH = window.innerHeight
    const ratio = vW / vH
    el.dataset.orygX = rect.x
    el.dataset.orygY = rect.y
    el.dataset.directionX = rect.x < vW - (rect.x + rect.width) ? -1 : 1
    el.dataset.directionY = rect.y < vH - (rect.y + rect.width) ? -1 : 1
    // position from center
    el.dataset.centerDistX = (rect.x + rect.width / 2) -  vW / 2
    el.dataset.centerDistY = (rect.y + rect.height / 2) -  vH / 2
    // array of distances from viewport edge [top, right, bottom, left]
    const distArr = [rect.y * ratio, vW - (rect.x + rect.width), (vH - (rect.y + rect.width)) * ratio, rect.x]
    const min = Math.min(...distArr)
    // direction: 0: top, 1: right, 2: bottom, 3: left
    switch (distArr.indexOf(min)) {
      case 1:
        el.dataset.direction = "right"
        break;
      case 2:
        el.dataset.direction = "bottom"
        break;
      case 3:
        el.dataset.direction = "left"
        break;
      default:
        el.dataset.direction = "top"
        break;
    }
  }
  return el
}
// set playground bg color
const setBgColor = (color) => {
  const wrapper = document.querySelector(".editionhome-playground")
  if (wrapper && color) {
    wrapper.style.backgroundColor = color
  }
}
// element animation SHOW
const showElement = (el, layer) => {
  const params = getElementParams(el)
  let time = getCurrentAnimTimeShow()
  if (!params.masker) {
    // randomize time
    const timeRandF = currentCompParams?.show?.animTimeRandom ? currentCompParams?.show?.animTimeRandom : 0
    const timeRandMax = time * timeRandF
    time = time - (Math.random() * timeRandMax)
  }
  const ease = currentCompParams?.show?.ease ? getTweenEaseFromString(currentCompParams?.show?.ease) : Expo.easeOut
  TweenMax.to(el, time, {x: 0, y:0, rotation: 0, scale: 1, ease })
}
// element animation HIDE
const hideElement = (el, layer) => {
  // check hover sound
  if (el.dataset.hoverSound) {
    stopSound(el.dataset.hoverSound)
    el.dataset.hoverSound = null
  }
  const params = getElementParams(el)
  const p = getElementStartPos(el, params)
  const ease = currentCompParams?.hide?.ease ? getTweenEaseFromString(currentCompParams?.hide?.ease) : Expo.easeOut
  const tp = {
    x: p.x, 
    y: p.y, 
    rotation: getElementOutOfVpRotation(el, params),
    ease
  }
  let time = getCurrentAnimTimeHide()
  if (!params.masker) {
    // randomize time
    const timeRandF = currentCompParams?.hide?.animTimeRandom ? currentCompParams?.hide?.animTimeRandom : 0
    const timeRandMax = time * timeRandF
    time = time - (Math.random() * timeRandMax)
  }
  TweenMax.to(el, time, tp)
}
// start element infinite animation
const startElementLevitation = (el, params) => {
  const t = getLevitationTween(params, el)
  if (t) {
    animations.push(t)
  }
}
// get the element's levitation tween
const getLevitationTween = (params, el) => {
  let t = null
  if (params.t) {
    // element has own props...
    // create tween
    let tp = {}
    let repeatRefresh = false
    tp.delay = params.delay ? params.delay : 0
    tp.rotation = params.r ? params.r : 0
    tp.scale = params.s ? params.s : 1
    tp.ease = getTweenEaseFromString(params.ease)  
    // random drift X
    if (params.randX) {
      const maxX = window.innerWidth * params.randX
      tp.x = 'random(-' + maxX + ', ' + maxX + ')'
      repeatRefresh = true
    }
    // random drift Y
    if (params.randY) {
      const maxY = window.innerHeight * params.randY
      tp.y = 'random(-' + maxY + ', ' + maxY + ')'
      repeatRefresh = true
    }
    // random Rotation
    if (params.randR) {
      tp.rotation = 'random(-' + params.randR + ', ' + params.randR + ')'
      repeatRefresh = true
    }
    // random Scale
    if (params.randS) {
      tp.scale = 'random(' + (1 - params.randS) + ', ' + (1 + params.randS) + ')'
      repeatRefresh = true
    }
    const subGroup = el.querySelector("g")
    // animation time
    let time = params.t
    const timeRandF = params?.randT ? params?.randT : 0
    const timeRandMax = time * timeRandF
    time = time - (Math.random() * timeRandMax)
    // repeat delay
    let repeatDelay = params.repeatDelay ? params.repeatDelay :  0
    const repeatDelF =  params?.randRepeatDelay ? params?.randRepeatDelay : 0
    const repeatDelMax = repeatDelay * repeatDelF
    repeatDelay = repeatDelay - (Math.random() * repeatDelMax)
    // kill tweens
    TweenMax.killTweensOf(subGroup)
    // prepare timeline animation
    t = new TimelineMax({
      repeat: -1,
      repeatRefresh,
      repeatDelay,
      yoyo: params.yoyo ? params.yoyo : false
    })
    t.to(subGroup, time, tp)
  }
  return t
}
// stop all animations
const stopAnimations = () => {
  animations.forEach((t) => {
    t.progress(0)
    t.kill()
  })
}

// get composition elements (svg's <g> params)
const getCompositionElements = (comp) => {
  return [...comp.querySelectorAll("svg g[data-wa-params]")]
}
// get composition element params
const getElementParams = (el) => {
  // merge comp params with el params
  const elParams = el ? JSON.parse(el.dataset.waParams) : {}
  const paramsDef = currentCompParams?.elDefaultParams && !elParams.t ? currentCompParams?.elDefaultParams : {}
  return {...paramsDef, ...elParams}
}
const getCompParams = (comp) => {
  return comp ? JSON.parse(comp.dataset.compParams) : {}
}
// get composition animation time SHOW
const getCurrentAnimTimeShow = () => {
  return currentCompParams?.show?.animTime ? currentCompParams?.show?.animTime : DEFAULT_TIME_ANIM_SHOW
}
// get composition animation time SHOW
const getCurrentAnimTimeHide = () => {
  return currentCompParams?.hide?.animTime ? currentCompParams?.hide?.animTime : DEFAULT_TIME_ANIM_HIDE
}
// get random out of vp rotation (based on comp params)
const getElementOutOfVpRotation = (el, params) => {
  let rotation = 0
  if (!params.masker) {
    const rotF = currentCompParams?.show?.rotation ? currentCompParams?.show?.rotation : .3
    const rotMax = 360 * rotF
    rotation = Math.random() * rotMax - (rotMax / 2)
  }
  return rotation
}

// ##########################
// ### DOM event handlers ###
const onDocClick = (e) => {
  // trace("doc click", e.target)
  transformCompositions()
  // hide INFO / MENU
  document.documentElement.classList.remove('playground-menu-open')
}
const onDocKeydown = (e) => {
  // check TAB
  if (e.keyCode != 9) {
    // trace("doc click", e.keyCode) 
    transformCompositions()
  }
}
// track mouse postion
const onDocMouseMove = (e) => {
  mouseP = {
    x: e.clientX,
    y: e.clientY
  }
  // const wW = window.innerWidth
  // const wH = window.innerHeight
  // const dist = {
  //   x: Math.abs(mouseP.x - wW * .5),
  //   y: Math.abs(mouseP.y - wH * .5),
  // }
  // const wDia = Math.ceil(Math.sqrt(wW * wW + wH * wH ))
  // const mDia = Math.ceil(Math.sqrt(dist.x * dist.x + dist.y * dist.y))
  // console.log(wDia, mDia)
  // if (mDia < wDia * .15) {
  //   document.body.classList.add("cursor-active")
  // } else {
  //   document.body.classList.remove("cursor-active")
  // }
}

// ########################
// ### ANIMATION FRAMES ###
// fps animation related
let now, delta
let interval = 1000/FPS
let then = Date.now()
// enterAnimationFrame (request animation frame + FPS)
const enterAnimationFrame = () => {
  if (!playing) {
    return
  }
  requestAnimationFrame(enterAnimationFrame)
  now = Date.now()
  delta = now - then
  if (delta > interval) {
    drawFrame()
    then = now - (delta % interval)
  }
}
// drawFrame frame... 
const drawFrame = () => {
  if (!currentComp) {
    return
  }
  // elements animation
  const elements = getCompositionElements(currentComp)
  elements.forEach(elementAnimationFrame)
}
// element response to gravity
const elementAnimationFrame = (el, layer) => {
  const elParams = getElementParams(el)
  const elGravityF = getElementGravityFactor(el, elParams)
  let isMoved = false
  let p = {x: 0, y: 0, r: 0}
  // check element mouse attraction factor
  if (elParams.mouseAttraction) {
    p = getElementMouseAttractionXY(el, elParams)
    isMoved = true
  }
  // add gravity
  if (elGravityF < -0.001 || elGravityF > 0.001) {
    p.x = p.x -el.dataset.centerDistX * elGravityF
    p.y = p.y -el.dataset.centerDistY * elGravityF
    isMoved = true
  }
  // ## check mouse interaction 
  if (elParams.mouseIA && el.matches(':hover')) {
    const chance = elParams.mouseIA.chance ? elParams.mouseIA.chance : 1
    const rand = Math.random() * 100
    if (rand <= 100 * chance) {
      const h = {
        x: elParams.mouseIA.x ? elParams.mouseIA.x : 0,
        y: elParams.mouseIA.y ? elParams.mouseIA.y : 0,
        r: elParams.mouseIA.r ? elParams.mouseIA.r : 0,
      }
      p.x = p.x + (Math.random() * (h.x * window.innerWidth) - (h.x * window.innerWidth) * .5)
      p.y = p.y + (Math.random() * (h.y * window.innerHeight) - (h.y * window.innerHeight) * .5)
      p.r = p.r + (Math.random() * h.r - (h.r * 0.5))
      isMoved = true
      // play sound?
      const soundFile = elParams.mouseIA.sounds ? (elParams.mouseIA.sounds[Math.floor(Math.random() * elParams.mouseIA.sounds.length)]) : null
      if (soundFile) {
        playSound(soundFile, false, {reverb: .7})
        // console.log(el.dataset.hoverSound)
      }
    }
  }
  // ## check mouse hover - singel schot
  if (elParams.hover) {
    if (el.matches(':hover')) {
      if (el.dataset.hoverFired != "1") {
        el.dataset.hoverFired = 1
        const soundFile = elParams.hover.sounds ? (elParams.hover.sounds[Math.floor(Math.random() * elParams.hover.sounds.length)]) : null
        const loop = elParams.hover.loop ? elParams.hover.loop : false
        if (soundFile) {
          el.dataset.hoverSound = soundFile
          playSound(soundFile, loop, {reverb: .7})
        }
      } 
    } else {
      el.dataset.hoverFired = 0
      if (el.dataset.hoverSound) {
        stopSound(el.dataset.hoverSound)
        el.dataset.hoverSound = null
      }
    }
  }
  if (isMoved) {
    const currentP = {
      x: gsap.getProperty(el, "x"),
      y: gsap.getProperty(el, "y")
    }
    // slow down movement
    const moveF = getCompositionMoveSpeedFactor()
    const moveP = {
      x: currentP.x + (p.x - currentP.x) * moveF,
      y: currentP.y + (p.y - currentP.y) * moveF,
    }
    TweenMax.killTweensOf(el)
    TweenMax.set(el, {x: moveP.x, y: moveP.y, rotation: p.r})
  }
}


// get element gravity factor
const getElementGravityFactor = (el, elParams) => {
  let elAttraction = elParams?.attraction ? elParams.attraction : 0
  if (elAttraction == 0) {
    // check default attraction
    elAttraction = currentCompParams?.elDefaultParams?.attraction ? currentCompParams?.elDefaultParams?.attraction : 0
  }
  return compositionGravity.attraction * elAttraction
}
// get element mouse attraction {X,Y}
const getElementMouseAttractionXY = (el, elParams) => {
  const rect = el.getBoundingClientRect()
  // el center point
  const cP = {
    x: parseInt(el.dataset.orygX) + rect.width / 2,
    y: parseInt(el.dataset.orygY) + rect.height / 2
  }
  // el distance from mouse
  const dist = {
    x: cP.x - mouseP.x,
    y: cP.y - mouseP.y,
    radius: Math.sqrt(Math.pow(cP.x - mouseP.x, 2) + Math.pow(cP.y - mouseP.y, 2))
  }
  // el attraction power
  const screenDiagonal = Math.sqrt(Math.pow(window.innerWidth, 2) + Math.pow(window.innerHeight, 2))
  const distF = (screenDiagonal - dist.radius) * 0.0001
  const compositionMousePower = getCompositionMouseAttractionPower()
  const elPower = -distF * elParams.mouseAttraction * compositionMousePower
  return {
    x: dist.x * elPower,
    y: dist.y * elPower
  }
}
const getCompositionMouseAttractionPower = () => {
  return currentCompParams?.mouseAttraction ? currentCompParams?.mouseAttraction : 1
}
const getCompositionMoveSpeedFactor = () => {
  return currentCompParams?.moveSpeedFactor ? currentCompParams?.moveSpeedFactor : 1
}



// ######################
// ### HELPER METHODS ###

// get ease function from params ease name
const getTweenEaseFromString = (easeName) => {
  switch (easeName) {
    case "Linear.easeNone":
      return Linear.easeNone
    case "Sine.easeIn":
      return Sine.easeIn
    case "Sine.easeOut":
      return Sine.easeOut
    case "Sine.easeInOut":
      return Sine.easeInOut
    case "Power0.easeIn":
      return Power0.easeIn
    case "Power0.easeOut":
      return Power0.easeOut
    case "Power0.easeInOut":
      return Power0.easeInOut
    case "Power1.easeIn":
      return Power1.easeIn
    case "Power1.easeOut":
      return Power1.easeOut
    case "Power1.easeInOut":
      return Power1.easeInOut
    case "Power2.easeIn":
      return Power2.easeIn
    case "Power2.easeOut":
      return Power2.easeOut
    case "Power2.easeInOut":
      return Power2.easeInOut
    case "Power3.easeIn":
      return Power3.easeIn
    case "Power3.easeOut":
      return Power3.easeOut
    case "Power3.easeInOut":
      return Power3.easeInOut
    case "Power4.easeIn":
      return Power4.easeIn
    case "Power4.easeOut":
      return Power4.easeOut
    case "Power4.easeInOut":
      return Power4.easeInOut
    case "Back.easeIn":
      return Back.easeIn
    case "Back.easeOut":
      return Back.easeOut
    case "Back.easeInOut":
      return Back.easeInOut
    case "Elastic.easeIn":
      return Elastic.easeIn
    case "Elastic.easeOut":
      return Elastic.easeOut
    case "Elastic.easeInOut":
    case "Bounce.easeIn":
      return Bounce.easeIn
    case "Bounce.easeOut":
      return Bounce.easeOut
    case "Bounce.easeInOut":
      return Bounce.easeInOut
    case "Expo.easeIn":
      return Expo.easeIn
    case "Expo.easeOut":
      return Expo.easeOut
    case "Expo.easeInOut":
      return Expo.easeInOut
    default:
      return Expo.easeInOut
  }
}